/* =========================================================================
   reAImagination · Particles / Rails engine
   ---------------------------------------------------------------------------
   Framework-agnostic. Pure Canvas 2D. No React / DOM dependencies.

   USAGE (React 19 + CRACO — inside any screen component):

     import { useEffect, useRef } from 'react';
     import { initParticles } from './particles';

     function Screen() {
       const canvasRef = useRef(null);
       useEffect(() => {
         const api = initParticles(canvasRef.current, { mode: 'cluster' });
         return () => api.destroy();         // <-- always clean up
       }, []);
       return <canvas id="px" ref={canvasRef} />;
     }

   Or for the fixed full-viewport background (recommended — mount once in App.jsx):

     useEffect(() => {
       const cv = document.getElementById('px');
       const api = initParticles(cv, { mode: 'ambient' });
       window.__particles = api;             // lets screens call api.setMode()
       return () => api.destroy();
     }, []);

   API:
     initParticles(canvas, opts?) -> {
       setMode(mode, data?),   // 'ambient' | 'cluster' | 'rails' | 'sort' | 'recompose'
       resize(),               // force re-measure (called automatically on window resize)
       pause(),
       play(),
       destroy(),
     }

   The 4B palette is sourced from CSS custom properties at init time, so it
   tracks token changes in tokens.css.
   ========================================================================= */

const DEFAULT_OPTS = {
  mode: 'ambient',
  density: 0.00012,     // particles per pixel² — tuned for dense-but-not-busy
  maxParticles: 280,
  speed: 0.35,
  linkRadius: 130,      // distance under which particles draw linking lines
  linkOpacity: 0.08,
  respectReducedMotion: true,
};

function readCSSVar(name, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function initParticles(canvas, userOpts = {}) {
  if (!canvas) throw new Error('initParticles: canvas element required');
  const opts = { ...DEFAULT_OPTS, ...userOpts };
  const ctx = canvas.getContext('2d');

  // Token-sourced palette (4B colors)
  const PAL = {
    bot:    readCSSVar('--bot',    '#00D4C8'),
    borrow: readCSSVar('--borrow', '#06B6D4'),
    build:  readCSSVar('--build',  '#C9A84C'),
    bi:     readCSSVar('--bi',     '#E8B96A'),
    buy:    readCSSVar('--buy',    '#EC4899'),
    teal:   readCSSVar('--teal',   '#00D4C8'),
    line:   'rgba(255,255,255,0.08)',
  };
  const COLORS = [PAL.bot, PAL.borrow, PAL.build, PAL.bi, PAL.buy];

  // State ------------------------------------------------------------------
  let W = 0, H = 0, dpr = 1;
  let particles = [];
  let rails = [];         // [{x1,y1,x2,y2,color,progress[0..1]}]
  let mode = opts.mode;
  let running = true;
  let rafId = null;
  let t = 0;              // global time in seconds
  const prefersReduced = opts.respectReducedMotion &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Resize -------------------------------------------------------------
  function measure() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    W = rect.width;
    H = rect.height;
    canvas.width  = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    rebuild();
  }

  // --- Particles ----------------------------------------------------------
  function makeParticle(i, count) {
    return {
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * opts.speed,
      vy: (Math.random() - 0.5) * opts.speed,
      r: 0.9 + Math.random() * 1.6,
      c: COLORS[i % COLORS.length],
      /* for cluster / rails modes: home position + animation tween */
      hx: 0, hy: 0,
      // Per-particle phase for subtle pulsing
      phase: Math.random() * Math.PI * 2,
    };
  }

  function rebuild() {
    const target = Math.min(
      opts.maxParticles,
      Math.max(60, Math.floor(W * H * opts.density))
    );
    if (particles.length === target) return;
    if (particles.length < target) {
      for (let i = particles.length; i < target; i++) particles.push(makeParticle(i, target));
    } else {
      particles.length = target;
    }
    assignHomes();
  }

  // --- Modes --------------------------------------------------------------
  function assignHomes() {
    const cx = W / 2, cy = H / 2;
    if (mode === 'cluster') {
      // Pulsing organ cluster — particles orbit center at varying radii
      particles.forEach((p, i) => {
        const ang = (i / particles.length) * Math.PI * 2;
        const rad = 60 + (i % 7) * 18;
        p.hx = cx + Math.cos(ang) * rad;
        p.hy = cy + Math.sin(ang) * rad;
      });
    } else if (mode === 'rails') {
      // 4 horizontal rails across the stage
      const railY = [H*0.28, H*0.44, H*0.60, H*0.76];
      particles.forEach((p, i) => {
        const lane = i % railY.length;
        p.hx = (i / particles.length) * W;
        p.hy = railY[lane];
        p.c = COLORS[lane];
      });
      rails = railY.map((y, i) => ({
        x1: 40, y1: y, x2: W - 40, y2: y, color: COLORS[i], progress: 0,
      }));
    } else if (mode === 'sort') {
      // 5 vertical destination columns (Bot / Borrow / Build / BI / Buy)
      const colX = [0.12, 0.30, 0.50, 0.70, 0.88].map(f => f * W);
      particles.forEach((p, i) => {
        const col = i % 5;
        p.hx = colX[col] + (Math.random() - 0.5) * 40;
        p.hy = H * 0.35 + (i % 40) * 6;
        p.c = COLORS[col];
      });
    } else if (mode === 'recompose') {
      // 2 target nuclei (new roles) — particles converge
      const t1 = { x: W * 0.33, y: H * 0.5 };
      const t2 = { x: W * 0.67, y: H * 0.5 };
      particles.forEach((p, i) => {
        const target = i % 2 === 0 ? t1 : t2;
        const ang = Math.random() * Math.PI * 2;
        const rad = 30 + Math.random() * 70;
        p.hx = target.x + Math.cos(ang) * rad;
        p.hy = target.y + Math.sin(ang) * rad;
      });
    } else {
      // ambient — no home, free drift
      rails = [];
    }
  }

  // --- Draw ---------------------------------------------------------------
  function step(dt) {
    t += dt;
    ctx.clearRect(0, 0, W, H);

    // Rails (drawn under particles)
    if (rails.length) {
      ctx.lineWidth = 1;
      rails.forEach(r => {
        const grad = ctx.createLinearGradient(r.x1, 0, r.x2, 0);
        grad.addColorStop(0, 'rgba(255,255,255,0.03)');
        grad.addColorStop(0.5, hexToRgba(r.color, 0.22));
        grad.addColorStop(1, 'rgba(255,255,255,0.03)');
        ctx.strokeStyle = grad;
        ctx.beginPath();
        ctx.moveTo(r.x1, r.y1);
        ctx.lineTo(r.x2, r.y2);
        ctx.stroke();
      });
    }

    // Particles
    const lr  = opts.linkRadius;
    const lr2 = lr * lr;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];

      if (mode === 'ambient') {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      } else {
        // ease toward home
        const k = prefersReduced ? 0.02 : 0.045;
        p.x += (p.hx - p.x) * k + Math.sin(t * 1.1 + p.phase) * 0.25;
        p.y += (p.hy - p.y) * k + Math.cos(t * 0.9 + p.phase) * 0.25;
      }

      // Linking lines — ambient & cluster only
      if (mode === 'ambient' || mode === 'cluster') {
        for (let j = i + 1; j < particles.length; j++) {
          const q  = particles[j];
          const dx = p.x - q.x, dy = p.y - q.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < lr2) {
            const a = (1 - d2 / lr2) * opts.linkOpacity;
            ctx.strokeStyle = `rgba(255,255,255,${a.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }

      // Dot + soft halo
      const pulse = 1 + Math.sin(t * 2 + p.phase) * 0.12;
      ctx.fillStyle = hexToRgba(p.c, 0.12);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * 4 * pulse, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = p.c;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * pulse, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // --- Loop ---------------------------------------------------------------
  let last = performance.now();
  function frame(now) {
    if (!running) return;
    const dt = Math.min(0.033, (now - last) / 1000);
    last = now;
    step(dt);
    rafId = requestAnimationFrame(frame);
  }

  // --- helpers ------------------------------------------------------------
  function hexToRgba(hex, a) {
    // supports #rrggbb
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  // --- lifecycle ---------------------------------------------------------
  function pause() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
  }

  function play() {
    if (!running) {
      running = true;
      last = performance.now();
      rafId = requestAnimationFrame(frame);
    }
  }

  const onResize = () => measure();
  const onVisibility = () => {
    if (document.hidden) pause();
    else play();
  };
  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVisibility);

  measure();
  rafId = requestAnimationFrame(frame);

  return {
    setMode(nextMode, data) {
      mode = nextMode;
      if (data && Array.isArray(data.rails)) rails = data.rails;
      assignHomes();
    },
    resize: measure,
    pause,
    play,
    destroy() {
      pause();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      particles = [];
      rails = [];
    },
  };
}
